import { create } from './lib/create'
import { isSoundEnabled, setSoundEnabled } from './audio/sound'

const STORAGE_KEY = 'trainGameSettings'

export interface SettingsCallbacks {
  onRestart: () => void
  onMenu: () => void
  onTrainIntervalChange: (interval: number) => void
}

export interface SettingsState {
  trainInterval: number
  soundEnabled: boolean
}

// Load settings from localStorage
export function loadSettings(): Partial<SettingsState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.warn('Failed to load settings from localStorage', e)
  }
  return {}
}

// Save settings to localStorage
function saveSettings(settings: SettingsState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (e) {
    console.warn('Failed to save settings to localStorage', e)
  }
}

export function createSettingsUI(container: HTMLElement, callbacks: SettingsCallbacks, initialState: SettingsState) {
  let isOpen = false
  
  // Load saved settings and merge with initial state
  const savedSettings = loadSettings()
  let trainInterval = savedSettings.trainInterval ?? initialState.trainInterval
  
  // Apply saved sound setting
  if (savedSettings.soundEnabled !== undefined) {
    setSoundEnabled(savedSettings.soundEnabled)
  }
  
  // Settings button (gear icon)
  const settingsBtn = create('button', {
    className: 'settings-btn',
    innerHTML: '⚙️',
    title: 'Settings',
    onclick: () => toggleSettings(),
  })
  
  // Settings panel
  const settingsPanel = create('div', {
    className: 'settings-panel',
    style: { display: 'none' },
  })
  
  // Panel header
  const header = create('div', {
    className: 'settings-header',
  }, [
    create('span', { textContent: 'Settings' }),
    create('button', {
      className: 'settings-close',
      textContent: '×',
      onclick: () => toggleSettings(),
    }),
  ])
  
  // Helper to save current settings
  const persistSettings = () => {
    saveSettings({
      trainInterval,
      soundEnabled: isSoundEnabled(),
    })
  }

  // Sound toggle
  const soundRow = create('div', { className: 'settings-row' }, [
    create('span', { textContent: 'Sound Effects' }),
    create('button', {
      className: `settings-toggle ${isSoundEnabled() ? 'active' : ''}`,
      textContent: isSoundEnabled() ? 'ON' : 'OFF',
      onclick: (e) => {
        const btn = e.target as HTMLButtonElement
        const newState = !isSoundEnabled()
        setSoundEnabled(newState)
        btn.textContent = newState ? 'ON' : 'OFF'
        btn.className = `settings-toggle ${newState ? 'active' : ''}`
        persistSettings()
      },
    }),
  ])
  
  // Train interval slider
  const intervalRow = create('div', { className: 'settings-row settings-row-vertical' }, [
    create('span', { textContent: `Train Interval: ${trainInterval.toFixed(1)}s` }),
    create('input', {
      type: 'range',
      min: '0.5',
      max: '5',
      step: '0.5',
      value: String(trainInterval),
      className: 'settings-slider',
      oninput: (e) => {
        const input = e.target as HTMLInputElement
        trainInterval = parseFloat(input.value)
        const label = intervalRow.querySelector('span')!
        label.textContent = `Train Interval: ${trainInterval.toFixed(1)}s`
        callbacks.onTrainIntervalChange(trainInterval)
        persistSettings()
      },
    }),
  ])
  
  // Action buttons
  const actionsRow = create('div', { className: 'settings-actions' }, [
    create('button', {
      className: 'settings-action-btn restart-btn',
      textContent: '🔄 Restart',
      onclick: () => {
        toggleSettings()
        callbacks.onRestart()
      },
    }),
    create('button', {
      className: 'settings-action-btn menu-btn',
      textContent: '🏠 Menu',
      onclick: () => {
        toggleSettings()
        callbacks.onMenu()
      },
    }),
  ])
  
  settingsPanel.appendChild(header)
  settingsPanel.appendChild(soundRow)
  settingsPanel.appendChild(intervalRow)
  settingsPanel.appendChild(actionsRow)
  
  container.appendChild(settingsBtn)
  container.appendChild(settingsPanel)
  
  function toggleSettings() {
    isOpen = !isOpen
    settingsPanel.style.display = isOpen ? 'flex' : 'none'
    settingsBtn.style.display = isOpen ? 'none' : 'block'
  }
  
  return {
    destroy: () => {
      settingsBtn.remove()
      settingsPanel.remove()
    },
    close: () => {
      if (isOpen) toggleSettings()
    },
    getTrainInterval: () => trainInterval,
  }
}
